import {
  useDeleteProjectMembersMutation,
  useInsertProjectMembersMutation,
} from '@crew/apis/project/projectApis'
import { Project, ProjectMember } from '@crew/models/domain'
import { NotifyEventType } from 'enums/app'
import {
  ObjectEventMessage,
  notifyProjectEvent,
  notifyProjectMemberEvent,
} from 'features/app/states/appSlice'

import { useCallback } from 'react'
import { useAppDispatch } from 'states/hooks'
import { Roles } from '@crew/enums/app'
import { RoleType } from '@crew/enums/domain'
import { useGetLookupRolesQuery } from '@crew/apis/lookup/lookupApis'

export const useGroupChatMemberListToolbar = () => {
  const dispatch = useAppDispatch()

  // プロジェクトのロール情報を取得
  const { data: getRoles } = useGetLookupRolesQuery({
    roleType: RoleType.Project,
  })

  // プロジェクトメンバー登録API
  const [
    insertProjectMembersMutation,
    { isLoading: isLoadingInsertGroupMember },
  ] = useInsertProjectMembersMutation()
  // 複数プロジェクトメンバー削除API
  const [
    deleteProjectMembersMutation,
    { isLoading: isLoadingDeleteGroupMembers },
  ] = useDeleteProjectMembersMutation()

  // メンバー登録処理
  const insertGroupMembers = useCallback(
    async (projectId: string, userIds: string[]) => {
      // 管理者ロールのIDを取得
      const adminRoleId = getRoles?.roles.find(
        (role) => role.roleCode === Roles.PrjAdmin.value
      )?.id
      if (!adminRoleId) {
        throw new Error('Admin role not found')
      }

      const payload = {
        projectId: projectId,
        users: userIds.map((user) => ({
          userId: user,
          roleId: adminRoleId,
        })),
      }

      await insertProjectMembersMutation({
        projectMembers: payload,
      }).unwrap()

      // メンバーが追加されたことを通知するイベントをトリガー
      const objectEventMessageGroupMember: ObjectEventMessage<ProjectMember> = {
        eventType: NotifyEventType.Inserted,
        id: projectId,
        object: undefined,
      }
      dispatch(notifyProjectMemberEvent(objectEventMessageGroupMember))

      // ヘッダーのメンバーリストを更新するイベントをトリガー
      const objectEventMessageProject: ObjectEventMessage<Project> = {
        eventType: NotifyEventType.Updated,
        id: projectId,
        object: {
          id: projectId,
        },
      }
      dispatch(notifyProjectEvent(objectEventMessageProject))
    },
    [dispatch, getRoles?.roles, insertProjectMembersMutation]
  )

  // 複数メンバー削除処理
  const deleteGroupMembers = useCallback(
    async (projectId: string, userIds: string[]) => {
      // API実行
      await deleteProjectMembersMutation({
        projectId,
        userIds,
      }).unwrap()

      // メンバーの削除イベントをトリガーする
      const objectEventMessageGroupChatMember: ObjectEventMessage<ProjectMember> =
        {
          eventType: NotifyEventType.Deleted,
          id: projectId,
          object: undefined,
        }
      dispatch(notifyProjectMemberEvent(objectEventMessageGroupChatMember))

      // ヘッダーのメンバーリストを更新するイベントをトリガーする
      const objectEventMessageGroupChat: ObjectEventMessage<Project> = {
        eventType: NotifyEventType.Updated,
        id: projectId,
        object: {
          id: projectId,
        },
      }
      dispatch(notifyProjectEvent(objectEventMessageGroupChat))
    },
    [deleteProjectMembersMutation, dispatch]
  )

  return {
    insertGroupMembers,
    isLoadingInsertGroupMember,
    deleteGroupMembers,
    isLoadingDeleteGroupMembers,
  }
}
